import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

// Add consistent styling matching homepage-demo.html
const addConsistentStyles = () => {
  if (document.getElementById('signup-modal-styles')) return;

  const style = document.createElement('style');
  style.id = 'signup-modal-styles';
  style.textContent = `
    .font-playfair { font-family: 'Playfair Display', serif; }
    .font-dm-sans { font-family: 'DM Sans', sans-serif; }
    .text-primary { color: #1CB7C8; }
    .bg-primary { background: linear-gradient(135deg, #1CB7C8 0%, #159BA9 100%); }
    .bg-primary-light { background-color: rgba(28, 183, 200, 0.1); }
    .text-charcoal { color: #1A1A1A; }
    .border-primary { border-color: #1CB7C8; }
    .hover\\:bg-primary-dark:hover { background: linear-gradient(135deg, #159BA9 0%, #127A85 100%); }
    .hover\\:text-primary:hover { color: #1CB7C8; }
    .focus\\:ring-primary:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(28, 183, 200, 0.2);
      border-color: #1CB7C8;
    }
  `;
  document.head.appendChild(style);
};

const SignupPage = ({ onClose, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    grade: '',
    parentEmail: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { signUp } = useAuth();

  // Add consistent styles and fonts
  useEffect(() => {
    addConsistentStyles();

    // Add Google Fonts (Playfair Display & DM Sans)
    if (!document.getElementById('signup-modal-fonts')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'signup-modal-fonts';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
  }, []);

  // Grade options from Year 3 to Year 12
  const gradeOptions = [
    { value: '', label: 'Select Your Grade' },
    { value: '3', label: 'Year 3' },
    { value: '4', label: 'Year 4' },
    { value: '5', label: 'Year 5' },
    { value: '6', label: 'Year 6' },
    { value: '7', label: 'Year 7' },
    { value: '8', label: 'Year 8' },
    { value: '9', label: 'Year 9' },
    { value: '10', label: 'Year 10' },
    { value: '11', label: 'Year 11' },
    { value: '12', label: 'Year 12' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('Email is required');  
      return false;
    }
    
    if (!formData.grade) {
      setError('Please select your grade');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    // Validate parent email if provided
    if (formData.parentEmail && formData.parentEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.parentEmail.trim())) {
        setError('Please enter a valid parent email address');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const result = await signUp(
        formData.email, 
        formData.password, 
        formData.fullName, 
        formData.grade,
        formData.parentEmail.trim() || null
      );
      if (result.success) {
        setSuccess(result.message || 'Account created successfully! Please check your email to confirm your account.');
        setFormData({ 
          fullName: '', 
          email: '', 
          password: '', 
          confirmPassword: '',
          grade: '',
          parentEmail: ''
        });
      } else {
        setError(result.error || 'Signup failed');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackgroundClick}
      style={{ backdropFilter: 'blur(8px)' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 relative font-dm-sans" style={{ border: '1px solid rgba(209, 213, 219, 0.3)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img src="/maths_logo_teal.png" alt="MathsCampus Logo" className="h-14 w-auto" />
            <span className="ml-3 text-3xl font-playfair font-bold text-charcoal" style={{ letterSpacing: '-0.02em' }}>MathsCampus</span>
          </div>
          <h2 className="text-3xl font-playfair font-bold text-charcoal mb-3" style={{ letterSpacing: '-0.02em' }}>Create Account</h2>
          <p className="text-gray-600 text-base">Join MathsCampus and start your learning journey</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              value={formData.fullName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-primary focus:border-primary transition-all"
              placeholder="John Smith"
              style={{ fontSize: '15px' }}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-primary focus:border-primary transition-all"
              placeholder="your@email.com"
              style={{ fontSize: '15px' }}
            />
          </div>

          <div>
            <label htmlFor="grade" className="block text-sm font-semibold text-gray-700 mb-2">
              Select Your Grade *
            </label>
            <select
              id="grade"
              name="grade"
              value={formData.grade}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-primary focus:border-primary transition-all bg-white"
              style={{ fontSize: '15px' }}
            >
              {gradeOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={!option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="parentEmail" className="block text-sm font-semibold text-gray-700 mb-2">
              Parent/Guardian Email (Optional)
            </label>
            <input
              id="parentEmail"
              name="parentEmail"
              type="email"
              value={formData.parentEmail}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-primary focus:border-primary transition-all"
              placeholder="parent@example.com"
              style={{ fontSize: '15px' }}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-primary focus:border-primary transition-all"
              placeholder="Min. 6 characters"
              style={{ fontSize: '15px' }}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-primary focus:border-primary transition-all"
              placeholder="Confirm your password"
              style={{ fontSize: '15px' }}
            />
          </div>

          {/* Privacy Message */}
          <div className="text-xs text-gray-500 text-center px-4 py-3 bg-primary-light rounded-xl mt-2">
            We do not share your information with third parties. Parent emails are only used to send progress reports.
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all transform ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-primary-dark hover:scale-[1.02] shadow-lg hover:shadow-xl'
            }`}
            style={{ fontSize: '16px', marginTop: '1.5rem' }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-8 text-center">
          <div className="text-gray-600 text-base">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-primary hover:text-primary font-bold transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>

        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-700 transition-colors text-2xl font-light"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default SignupPage;